import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getpackage } from "../../store/slices/package";
import PackageStep from "./PackageStep";
import FerryStep from "./FerryStep";
import HotelStep from "./HotelStep";
import ActivityStep from "./ActivityStep";
import CarStep from "./CarStep";
import OptionStep from "./OptionStep";
import DayPlanStep from "./DayPlanStep";
import PricingStep from "./pricingStep";
import PackageStepper from "./PackageStepper";

const steps = [
  "Package",
  "Options",
  "Itinerary",
  "Hotel",
  "Activity",
  "Car",
  "Ferry",
  "Pricing",
];

export default function AddPackages() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const hasFetched = useRef(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [packageId, setPackageId] = useState(null);
  const [durationNights, setDurationNights] = useState(null);
  const [durationDays, setdurationDays] = useState(null);

  const [packageForm, setPackageForm] = useState({
    title: "",
    places: [],
    category: "",
    durationDays: "",
    durationNights: "",
    pricingType: "dynamic",
    basePax: 2,
    hotelRequired: true,
    description: "",
    inclusions: "",
    exclusions: "",
    paymentpolicy: "",
    image: [],
  });

  const [packageOptions, setPackageOptions] = useState([]);
  const [packageDays, setPackageDays] = useState([]);
  const [packageHotels, setPackageHotels] = useState([]);
  const [packageActivities, setPackageActivities] = useState([]);
  const [packageCars, setPackageCars] = useState([]);
  const [packageFerries, setPackageFerries] = useState([]);

  // ---------------- FETCH EDIT DATA ----------------
  useEffect(() => {
    if (!id) return;
    if (hasFetched.current) return;

    hasFetched.current = true;

    const fetchPackage = async () => {
      try {
        const res = await dispatch(getpackage({ id })).unwrap();
        const data = res?.data;
        if (!data) return;

        setPackageId(data._id);
        setDurationNights(data.durationNights);
        setdurationDays(data.durationDays);

        setPackageForm({
          title: data.title || "",
          places: data.places?.map((p) => p._id) || [],
          category: data.category?._id || "",
          durationDays: data.durationDays || "",
          durationNights: data.durationNights || "",
          pricingType: data.pricingType || "dynamic",
          basePax: data.basePax || 2,
          description: data.description || "",
          inclusions: data.inclusions || "",
          paymentpolicy: data.paymentpolicy || "",
          exclusions: data.exclusions || "",
          image: data.image || [],
          hotelRequired: data.hotelRequired ?? true,
        });

        setPackageOptions(data.options || []);
        setPackageDays(data.dayPlans || []);
        setPackageHotels(
          (data.hotels || []).map((h) => ({
            _id: h._id,
            packageOption: h.packageOption?._id || "",
            dayPlan: h.dayPlan?._id || "",
            place: h.hotel?.location?._id || "",
            hotel: h.hotel?._id || "",
            roomType: h.roomType?._id || "",

            nights: h.nights || 1,
            rooms: h.rooms || 1,
            extraBeds: h.extraBeds || 0,

            roomPrice: h.roomPrice || h.roomType?.basePrice || 0,
            extraBedPrice:
              h.extraBedPrice || h.roomType?.extraBedPrice || 0,

            // IMPORTANT: preserve saved hotel add-ons
            addons: Array.isArray(h.addons)
              ? h.addons.map((addon) => ({
                addonId: addon.addonId || null,
                name: addon.name || "",
                type: addon.type || "",
                price: Number(addon.price || 0),
                quantity: addon.quantity || 1,
                isIncluded: addon.isIncluded ?? true,
                _id: addon._id,
              }))
              : [],

            meals: h.meals || {
              breakfast: false,
              lunch: false,
              dinner: false,
            },
          })),
        );
        setPackageActivities(
          (data.activities || []).map((a) => ({
            _id: a._id,
            packageOption: a.packageOption?._id || "",
            dayPlan: a.dayPlan?._id || "",
            activities:
              a.activities?.map((act) => ({
                _id: act._id,
                activity: act.activity?._id || "",
                vendor: act.vendor?._id || "",
                timing: act.timing || "",
              })) || [],
          })),
        );

        setPackageCars(
          (data.vehicles || []).map((c) => ({
            _id: c._id,
            packageOption: c.packageOption?._id,
            dayPlan: c.dayPlan?._id,
            vehicleType: c.vehicleType?._id || "",
            pricePerDay: c?.pricePerDay,
          })),
        );
        setPackageFerries(
          (data.ferrys || []).map((doc) => ({
            _id: doc._id,
            packageOption: doc.packageOption?._id || "",
            dayPlan: doc.dayPlan?._id || "",

            ferries:
              doc.ferries?.map((f) => ({
                _id: f._id,

                // IMPORTANT
                ferry: f.ferry?._id || "",
                ferryId: f.ferry?._id || "",

                ferryName: f.ferry?.name || "",

                // Preserve schedules from API
                schedules: f.ferry?.schedules || [],

                fromPlace: f.fromPlace?._id || "",
                toPlace: f.toPlace?._id || "",

                scheduleIndex:
                  f.scheduleIndex !== undefined
                    ? Number(f.scheduleIndex)
                    : "",

                className: f.className || "",

                departureTime: f.departureTime || "",
                arrivalTime: f.arrivalTime || "",
                duration: f.duration || "",

                price: f.price || 0,
                quantity: f.quantity || 1,
              })) || [],
          })),
        );
      } catch (err) {
        console.error("Failed to fetch package:", err);
      }
    };

    fetchPackage();
  }, [id, dispatch]);

  // ---------------- UI ----------------
  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <Link to="/admin/packages" className="breadcrumb-link active">
            Packages
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span className="breadcrumb-link inactive">
            {id ? "Edit Package" : "Add Package"}
          </span>
        </div>
      </div>

      <PackageStepper
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        isEditable={!!id}
      />

      {currentStep === 0 && (!id || packageId) && (
        <PackageStep
          form={packageForm}
          setForm={setPackageForm}
          packageId={packageId}
          setPackageId={setPackageId}
          setDurationDays={setdurationDays}
          nextStep={() => setCurrentStep(1)}
        />
      )}
      {currentStep === 1 && (
        <OptionStep
          packageId={packageId}
          options={packageOptions}
          setOptions={setPackageOptions}
          nextStep={() => setCurrentStep(2)}
          prevStep={() => setCurrentStep(0)}
        />
      )}

      {currentStep === 2 && (
        <DayPlanStep
          packageId={packageId}
          durationDays={durationDays}
          days={packageDays}
          setDays={setPackageDays}
          nextStep={() =>
            setCurrentStep(packageForm.hotelRequired ? 3 : 4)
          }
          prevStep={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && (
        <HotelStep
          packageId={packageId}
          hotels={packageHotels}
          setHotels={setPackageHotels}
          nextStep={() => setCurrentStep(4)}
          prevStep={() => setCurrentStep(2)}
        />
      )}

      {currentStep === 4 && (
        <ActivityStep
          packageId={packageId}
          activities={packageActivities}
          setActivities={setPackageActivities}
          nextStep={() => setCurrentStep(5)}
          prevStep={() => setCurrentStep(3)}
        />
      )}

      {currentStep === 5 && (
        <CarStep
          packageId={packageId}
          cars={packageCars}
          setCars={setPackageCars}
          nextStep={() => setCurrentStep(6)}
          prevStep={() => setCurrentStep(4)}
        />
      )}

      {currentStep === 6 && (
        <FerryStep
          packageId={packageId}
          ferries={packageFerries}
          setFerries={setPackageFerries}
          nextStep={() => setCurrentStep(7)}
          prevStep={() => setCurrentStep(5)}
        />
      )}

      {currentStep === 7 && (
        <PricingStep packageId={packageId} prevStep={() => setCurrentStep(6)} />
      )}
    </>
  );
}